/**
 * Client-side mirror of the agent's step list.
 *
 * The server owns this data: `update_plan` writes it to `chats.metadata.plan`
 * and streams each change, and the runner settles it at turn end
 * (`packages/ai/src/core/plan/`). Everything here is read-only shape and
 * validation — no lifecycle, deliberately. See `store/plan-store.ts`.
 *
 * @module types/plan
 */

/**
 * AI SDK data-part type carrying a live list update.
 *
 * Still the CoWork-era name because the part is persisted on message rows, so
 * every existing chat holds it under this name. Renaming it is cosmetic and
 * costs a migration of historical rendering, so it is deferred.
 */
export const PLAN_DATA_TYPE = "data-cowork-plan" as const;

export type PlanStepStatus = "pending" | "in_progress" | "completed";

export interface PlanStep {
  title: string;
  status: PlanStepStatus;
}

/** The field is `tasks` for the same persistence reason as the type name above. */
export interface PlanPayload {
  chatId?: string;
  explanation?: string;
  tasks: PlanStep[];
}

const STATUSES: PlanStepStatus[] = ["pending", "in_progress", "completed"];

/** Runtime guard for a streamed or server-provided plan payload. */
export function isPlanPayload(value: unknown): value is PlanPayload {
  if (!value || typeof value !== "object") return false;
  const p = value as { tasks?: unknown };
  // An empty step list is valid — it means "no plan" (trivial request).
  return (
    Array.isArray(p.tasks) &&
    p.tasks.every(
      (t) =>
        !!t &&
        typeof t === "object" &&
        typeof (t as PlanStep).title === "string" &&
        STATUSES.includes((t as PlanStep).status),
    )
  );
}
